// File: src/services/coachService.ts
// ==============================================================================
// VERSION INFORMATION
// ==============================================================================
// __version__ = "1.2.0"
//
// Changelog:
//
// Version 1.2.0:
// - [Added] New `getLatestActivityForAllClients` function to use the efficient
//   bulk-fetch endpoint for the coach dashboard.
// - [Deprecated] Removed the `getLatestClientActivity` function, which is now
//   obsolete and caused N+1 request issues.
//
// Version 1.1.0:
// - [Added] Client group management functions (create, get, update, delete).
//
// Version 1.0.0:
// - Initial version of the coach service.
// ==============================================================================
import apiClient from './api';
import { CoachProfile, ClientProfile, InvitationLink, User, MsgResponse, ActivityFeed, ClientActivitySummary, AIActivitySummaryResponse, ClientActivitySummaryUpdatePayload, ShowcaseClientCreate, ShowcaseClientTaskResponse, ActivityFeed as ActivityFeedType, ClientGroup } from '../types';
import { AxiosError } from 'axios';

// Payload for creating an invitation
export interface CoachInvitationCreatePayload {
  invitee_email: string;
  invitee_first_name?: string | null;
  invitee_last_name?: string | null;
}

export interface ClientActivitySummaryRequestPayload {
  client_user_id: number;
  start_date: string; // YYYY-MM-DD
  end_date: string;   // YYYY-MM-DD
}

// Payload for updating coach profile
export interface CoachProfileUpdatePayload {
  bio?: string | null;
  specialties?: string[] | null;
  experience?: string | null;
  certifications?: string[] | null; 
  website_url?: string | null;
  public_profile_enabled?: boolean;
  phone_number?: string | null;
  city?: string | null;
  country?: string | null;
  public_contact_email?: string | null;
  niche_focus_areas?: string[] | null;
  languages_spoken?: string[] | null;
  years_of_experience?: number | null;
  introductory_video_url?: string | null;
  is_portrait_video?: boolean; 
  social_media_links?: Record<string, string> | null;
}

// --- NEW PROGRAM APPLICATION PAYLOAD ---
export interface ProgramItemConfiguration {
    item_template_id: number;
    item_template_type: 'goal' | 'habit';
    start_date: string | null;
    target_date?: string | null; // For goals
    end_date?: string | null;   // For habits
    category_id?: number | null; // ADDED: Allow category override
}

export interface ApplyProgramPayload {
  program_template_id: number;
  configurations: ProgramItemConfiguration[];
}

// --- NEW GROUP PAYLOADS ---
export interface ClientGroupCreatePayload {
    name: string;
    description?: string | null;
    client_ids?: number[];
}

export interface ClientGroupUpdatePayload {
    name?: string;
    description?: string | null;
    client_ids?: number[];
}


const getMyProfile = async (): Promise<CoachProfile> => {
  const { data } = await apiClient.get<CoachProfile>('/coaches/me/profile');
  return data;
};

const updateMyProfile = async (payload: CoachProfileUpdatePayload): Promise<CoachProfile> => {
  const { data } = await apiClient.put<CoachProfile>('/coaches/me/profile', payload);
  return data;
};

const getClients = async (skip = 0, limit = 100): Promise<ClientProfile[]> => {
  const { data } = await apiClient.get<ClientProfile[]>('/coaches/me/clients', { params: { skip, limit } });
  return data;
};

const getClientById = async (clientUserId: number): Promise<User> => {
  const { data } = await apiClient.get<User>(`/coaches/me/clients/${clientUserId}`);
  return data;
};

const createInvitation = async (payload: CoachInvitationCreatePayload): Promise<InvitationLink> => {
  const { data } = await apiClient.post<InvitationLink>('/coaches/me/invitations', payload);
  return data;
};

const getPendingInvitations = async (skip = 0, limit = 100): Promise<InvitationLink[]> => {
  const { data } = await apiClient.get<InvitationLink[]>('/coaches/me/invitations/pending', { params: { skip, limit } });
  return data;
};

const revokeInvitation = async (invitationId: number): Promise<InvitationLink> => {
  const { data } = await apiClient.patch<InvitationLink>(`/coaches/me/invitations/${invitationId}/revoke`);
  return data;
};

const unassignClient = async (clientUserId: number): Promise<MsgResponse> => {
  const { data } = await apiClient.delete<MsgResponse>(`/coaches/me/clients/${clientUserId}/unassign`);
  return data;
};

const getPublicCoaches = async (): Promise<CoachProfile[]> => {
    const { data } = await apiClient.get<CoachProfile[]>('/coaches/public');
    return data;
};

const getPublicCoachProfileById = async (coachId: number): Promise<CoachProfile> => {
    const { data } = await apiClient.get<CoachProfile>(`/coaches/public/${coachId}`);
    return data;
};

// --- Client Activity Feed ---
const getClientActivityFeed = async (clientId: number, skip = 0, limit = 50): Promise<ActivityFeed[]> => {
  const { data } = await apiClient.get<ActivityFeed[]>(`/coaches/me/clients/${clientId}/activity-feed`, {
    params: { skip, limit }
  });
  return data;
};

const markActivityFeedItemAsRead = async (activityId: number): Promise<ActivityFeed> => {
  const { data } = await apiClient.post<ActivityFeed>(`/coaches/me/activity-feed/${activityId}/read`);
  return data;
};

const requestClientActivitySummary = async (payload: ClientActivitySummaryRequestPayload): Promise<AIActivitySummaryResponse> => {
  const { data } = await apiClient.post<AIActivitySummaryResponse>('/coaches/me/clients/summarize-activity', payload);
  return data;
};

const getClientActivitySummaries = async (clientId: number): Promise<ClientActivitySummary[]> => {
  const { data } = await apiClient.get<ClientActivitySummary[]>(`/coaches/me/clients/${clientId}/summaries`);
  return data;
};

const getClientActivitySummaryById = async (summaryId: number): Promise<ClientActivitySummary> => {
  const { data } = await apiClient.get<ClientActivitySummary>(`/coaches/me/clients/summaries/${summaryId}`);
  return data;
};

const updateActivitySummary = async (summaryId: number, payload: ClientActivitySummaryUpdatePayload): Promise<ClientActivitySummary> => {
  const { data } = await apiClient.put<ClientActivitySummary>(`/coaches/me/clients/summaries/${summaryId}`, payload);
  return data;
};

const applyProgramToClient = async (clientId: number, payload: ApplyProgramPayload): Promise<MsgResponse> => {
  const { data } = await apiClient.post<MsgResponse>(`/clients/${clientId}/apply-program`, payload);
  return data;
};

const nudgeClient = async (clientId: number): Promise<MsgResponse> => {
    const { data } = await apiClient.post<MsgResponse>(`/coaches/me/clients/${clientId}/nudge`);
    return data;
};

const createShowcaseClient = async (payload: ShowcaseClientCreate): Promise<ShowcaseClientTaskResponse> => {
    const { data } = await apiClient.post<ShowcaseClientTaskResponse>('/coaches/me/showcase-client', payload, {
        timeout: 180000 // 3 minutes timeout
    });
    return data;
};

const getShowcaseClient = async (): Promise<User | null> => {
    try {
        const { data } = await apiClient.get<User>('/coaches/me/showcase-client');
        return data;
    } catch (error) {
        const axiosError = error as AxiosError;
        if (axiosError.response && axiosError.response.status === 404) {
            return null;
        }
        throw error;
    }
};

// --- NEW: Bulk fetch for latest activity ---
const getLatestActivityForAllClients = async (): Promise<ActivityFeedType[]> => {
    const { data } = await apiClient.get<ActivityFeedType[]>(`/coaches/me/clients/activity-feed/latest-all`);
    return data;
};

// --- NEW: Client Group Management ---
const createGroup = async (payload: ClientGroupCreatePayload): Promise<ClientGroup> => {
    const { data } = await apiClient.post<ClientGroup>('/coaches/me/groups', payload);
    return data;
};

const getGroups = async (): Promise<ClientGroup[]> => {
    const { data } = await apiClient.get<ClientGroup[]>('/coaches/me/groups');
    return data;
};

const getGroupById = async (groupId: number): Promise<ClientGroup> => {
    const { data } = await apiClient.get<ClientGroup>(`/coaches/me/groups/${groupId}`);
    return data;
};

const updateGroup = async (groupId: number, payload: ClientGroupUpdatePayload): Promise<ClientGroup> => {
    const { data } = await apiClient.put<ClientGroup>(`/coaches/me/groups/${groupId}`, payload);
    return data;
};

const deleteGroup = async (groupId: number): Promise<MsgResponse> => {
    const { data } = await apiClient.delete<MsgResponse>(`/coaches/me/groups/${groupId}`);
    return data;
};


export default {
  getMyProfile,
  updateMyProfile,
  getClients,
  getClientById, 
  createInvitation,
  getPendingInvitations,
  revokeInvitation,
  unassignClient,
  getPublicCoaches, 
  getPublicCoachProfileById, 
  getClientActivityFeed, 
  markActivityFeedItemAsRead, 
  requestClientActivitySummary,
  getClientActivitySummaries,
  getClientActivitySummaryById,
  updateActivitySummary,
  applyProgramToClient,
  nudgeClient,
  createShowcaseClient,
  getShowcaseClient,
  getLatestActivityForAllClients,
  // NEW
  createGroup,
  getGroups,
  getGroupById,
  updateGroup,
  deleteGroup,
};